import {
  type DocumentData,
  type Firestore,
  collection,
  doc,
  getDoc,
  serverTimestamp,
  setDoc,
  updateDoc,
} from 'firebase/firestore';
import {
  ConsentSchema,
  ConsentCreateInputSchema,
  ConsentUpdateInputSchema,
  type Consent,
  type ConsentCreateInput,
  type ConsentUpdateInput,
} from '@taki/data-model';
import { CURRENT_SCHEMA_VERSION, buildUpdateData, createConverter } from './standard-fields.js';
import {
  type CountOptions,
  type ListOptions,
  runAverage,
  runCount,
  runList,
  runSum,
} from './list-query.js';
import { createArchiveOps } from './archive-ops.js';

/**
 * Repository de la famille `consent` (consentement horodaté, org-wide en v0).
 *
 * Calque le patron `asset` : l'entrée est VALIDÉE par Zod
 * (`ConsentCreateInputSchema`) AVANT toute écriture Firestore. Les champs
 * standard sont remplis par le SDK, les requêtes `list` sont cadrées par orgId.
 *
 * Sécurité / RGPD : le consentement vaut preuve, il est append-only. Le SDK
 * n'expose donc que create/list/get : la révocation (seul `revokedAt`
 * modifiable) et l'interdiction de suppression sont portées par les Security
 * Rules. Org-wide en v0 (même doctrine qu'asset) ; la lecture fine par rôle
 * viendra après.
 */

export type { ConsentCreateInput };

/** Nom de la collection Firestore de la famille consent. */
export const CONSENTS_COLLECTION = 'consents';

/**
 * Repository typé pour la famille `consent`.
 * Toute lecture est validée par `ConsentSchema`, toute écriture par
 * `ConsentCreateInputSchema` (cage IA : aucun accès Firestore direct hors SDK).
 */
export interface ConsentRepository {
  create(input: ConsentCreateInput): Promise<Consent>;
  list(options?: ListOptions): Promise<Consent[]>;
  count(options?: CountOptions): Promise<number>;
  /** Somme serveur d'un champ, memes options que `count`. Cf. list-query.ts. */
  sum(field: string, options?: CountOptions): Promise<number>;
  /** Moyenne serveur d'un champ, `null` si aucun document. Cf. list-query.ts. */
  average(field: string, options?: CountOptions): Promise<number | null>;
  get(id: string): Promise<Consent | null>;
  update(id: string, patch: ConsentUpdateInput): Promise<Consent>;
  /** Archive (`archivedAt`). Semantique commune : cf. archive-ops.ts. */
  archive(id: string): Promise<Consent>;
  /** Desarchive (`archivedAt` = null, JAMAIS deleteField). Cf. archive-ops.ts. */
  unarchive(id: string): Promise<Consent>;
  /** Supprime (`deletedAt` + `archivedAt`), invisible cote client. Cf. archive-ops.ts. */
  remove(id: string): Promise<Consent>;
}

const consentConverter = createConverter<Consent>((raw: DocumentData) => ConsentSchema.parse(raw));

/**
 * @param getActorId - retourne l'uid de l'utilisateur authentifié courant
 *   (injecté par `createTakiSdk`). Les Security Rules exigent
 *   createdBy/updatedBy == request.auth.uid.
 */
export function createConsentRepository(
  db: Firestore,
  getActorId: () => string,
  orgId: string,
): ConsentRepository {
  const col = collection(db, CONSENTS_COLLECTION).withConverter(consentConverter);
  // Archiver, desarchiver, supprimer : la semantique des trois etats vit dans
  // `createArchiveOps` (archive-ops.ts), ecrite une seule fois pour les 55
  // familles. Une famille n'a plus rien a decider ici.
  const archiveOps = createArchiveOps<Consent>(db, col, CONSENTS_COLLECTION, getActorId);

  return {
    async create(input: ConsentCreateInput): Promise<Consent> {
      // Valider AVANT d'écrire : une entrée invalide échoue ici, rien n'atteint
      // Firestore. (Même doctrine que le patron asset.)
      const fields = ConsentCreateInputSchema.parse(input);

      const actor = getActorId();
      const ref = doc(col);
      const now = new Date();

      // Champs métier optionnels : on ne les écrit que s'ils sont fournis
      // (évite d'ancrer des `undefined` / `null` parasites dans Firestore).
      const optional: DocumentData = {};
      if (fields.givenAt !== undefined) optional['givenAt'] = fields.givenAt;
      if (fields.revokedAt !== undefined) optional['revokedAt'] = fields.revokedAt;
      if (fields.textVersion !== undefined) optional['textVersion'] = fields.textVersion;
      if (fields.proofDocumentId !== undefined) optional['proofDocumentId'] = fields.proofDocumentId;
      if (fields.channel !== undefined) optional['channel'] = fields.channel;
      if (fields.relationshipId !== undefined) optional['relationshipId'] = fields.relationshipId;
      if (fields.campaignId !== undefined) optional['campaignId'] = fields.campaignId;
      if (fields.messageId !== undefined) optional['messageId'] = fields.messageId;
      if (fields.customFields !== undefined) optional['customFields'] = fields.customFields;
      if (fields.couleur != null) optional['couleur'] = fields.couleur;
      if (fields.photo != null) optional['photo'] = fields.photo;

      const payload: DocumentData = {
        orgId: fields.orgId,
        type: fields.type,
        contactId: fields.contactId,
        scope: fields.scope,
        ...optional,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        createdBy: actor,
        updatedBy: actor,
        archivedAt: null,
        deletedAt: null,
        schemaVersion: CURRENT_SCHEMA_VERSION,
      };

      await setDoc(ref, payload as unknown as Consent);

      // setDoc ne résout pas serverTimestamp() : on valide une projection
      // cohérente avec ce qui vient d'être écrit.
      return ConsentSchema.parse({
        id: ref.id,
        ...payload,
        createdAt: now,
        updatedAt: now,
      });
    },

    async list(options?: ListOptions): Promise<Consent[]> {
      // Cadrage orgId, exclusion des archives, sous-type, tri, taille de page
      // et curseur : toute la semantique vit dans `runList` (list-query.ts),
      // ecrite une seule fois pour les 55 familles.
      return runList(col, orgId, options);
    },

    async count(options?: CountOptions): Promise<number> {
      // Compter sans charger : meme cadrage que `list`, sans page ni tri
      // (cf. runCount, list-query.ts). Un compteur calcule sur la page
      // courante ment des que la liste est paginee.
      return runCount(col, orgId, options);
    },

    async sum(field: string, options?: CountOptions): Promise<number> {
      // Sommer sans charger, meme cadrage que `count` (cf. runSum,
      // list-query.ts). Rend 0 quand aucun document ne passe le filtre.
      return runSum(col, orgId, field, options);
    },

    async average(field: string, options?: CountOptions): Promise<number | null> {
      // `null` quand aucun document ne passe le filtre : c'est ce que rend
      // Firestore, et un zero mentirait (cf. runAverage, list-query.ts).
      return runAverage(col, orgId, field, options);
    },

    async get(id: string): Promise<Consent | null> {
      const ref = doc(col, id);
      const snap = await getDoc(ref);
      if (!snap.exists()) return null;
      return snap.data();
    },

    async update(id: string, patch: ConsentUpdateInput): Promise<Consent> {
      // Valider AVANT d'ecrire (meme doctrine que create).
      const fields = ConsentUpdateInputSchema.parse(patch);
      const actor = getActorId();

      // Patch transforme pour updateDoc : customFields est eclate en chemins
      // pointes ('customFields.<cle>') pour fusionner cle par cle au lieu de
      // remplacer la map entiere (cf. buildUpdateData ; null = deleteField).
      const data = buildUpdateData(fields);
      data['updatedAt'] = serverTimestamp();
      data['updatedBy'] = actor;

      // Ref brute (sans converter) pour l'ecriture partielle : le converter
      // exigerait le document complet.
      await updateDoc(doc(db, CONSENTS_COLLECTION, id), data);

      // Relire le document reel via la ref convertie : source de verite.
      const snap = await getDoc(doc(col, id));
      if (!snap.exists()) {
        throw new Error(`consent ${id} introuvable apres mise a jour.`);
      }
      return snap.data();
    },

    // archive / unarchive / remove : cf. `archiveOps` plus haut.
    ...archiveOps,
  };
}
